import { api } from './client';
import type {
  PaginatedResponse,
  ServiceArea,
  SpecialistProfile,
  SpecialistSummary,
} from '@/types/domain';

export interface SearchParams {
  category?: string[];
  city?: string[];
  lang?: string;
  tag?: string[];
  q?: string;
  lat?: number;
  lng?: number;
  radius?: number;
  verified?: boolean;
  sort?: 'score' | 'recent' | 'name';
  cursor?: string | null;
  /** F-170 B3.2: filter specialists by `ServicePreset.location_kind`.
   *  Undefined or null = no filter (default "Будь-де"). */
  location?: 'online' | 'delivery' | 'onsite' | null;
}

function flatten(params: SearchParams): Record<string, string | number | boolean | undefined> {
  const flat: Record<string, string | number | boolean | undefined> = {};
  if (params.category?.length) flat.category = params.category.join(',');
  if (params.city?.length) flat.city = params.city.join(',');
  if (params.tag?.length) flat.tag = params.tag.join(',');
  if (params.lang) flat.lang = params.lang;
  if (params.q) flat.q = params.q;
  if (params.lat !== undefined) flat.lat = params.lat;
  if (params.lng !== undefined) flat.lng = params.lng;
  if (params.radius !== undefined) flat.radius = params.radius;
  if (params.verified !== undefined) flat.verified = params.verified;
  if (params.sort) flat.sort = params.sort;
  if (params.cursor) flat.cursor = params.cursor;
  if (params.location) flat.location = params.location;
  return flat;
}

export async function searchSpecialists(
  params: SearchParams,
): Promise<PaginatedResponse<SpecialistSummary>> {
  return api.get('/specialists/', { query: flatten(params) });
}

export async function fetchSpecialist(slug: string): Promise<SpecialistProfile> {
  return api.get(`/specialists/${slug}/`);
}

export async function fetchMySpecialist(): Promise<SpecialistProfile> {
  return api.get('/specialists/me/');
}

export async function updateMySpecialist(
  patch: Partial<SpecialistProfile>,
): Promise<SpecialistProfile> {
  // Empty patch is treated as a GET so callers can use this as a one-shot
  // "load me" without a separate import. Real partial updates use PATCH.
  if (!patch || Object.keys(patch).length === 0) {
    return api.get('/specialists/me/');
  }
  return api.patch('/specialists/me/', { body: patch });
}

export async function fetchMyStats(): Promise<{
  profile_views_7d: number;
  profile_views_30d: number;
  inquiry_count_30d: number;
  response_rate: number;
  avg_response_time_minutes: number;
  conversion_rate: number;
  top_review_tags: { slug: string; rate: number }[];
}> {
  return api.get('/specialists/me/stats/');
}

export async function trackProfileView(slug: string): Promise<void> {
  await api.post(`/specialists/${slug}/view/`);
}

/**
 * F-180 (B9) — upload a new avatar via multipart.
 * Backend pipes it through `process_image_upload` so the file lands
 * under `avatars/<uuid>.jpg` and the returned profile carries the new
 * `avatar_url`.
 */
export async function uploadAvatar(file: File): Promise<SpecialistProfile> {
  const form = new FormData();
  form.append('avatar', file);
  return api.post('/specialists/me/avatar/', { body: form });
}

export async function clearAvatar(): Promise<SpecialistProfile> {
  return api.del('/specialists/me/avatar/');
}

// ---------------------------------------------------------------------------
// F-132 self-service authoring endpoints (all mounted at /specialists/me/*).
// ---------------------------------------------------------------------------

export interface PortfolioRow {
  id: number;
  image_url: string;
  caption: string;
  sort_order: number;
  is_active: boolean;
}

export interface QnARow {
  id: number;
  question: string;
  answer: string;
  sort_order: number;
}

export interface CategoryRow {
  slug: string;
  name_ua: string;
  is_primary: boolean;
}

export interface CityRow {
  slug: string;
  name_ua: string;
  is_primary: boolean;
}

export interface LanguageRow {
  code: string;
  name_ua: string;
}

export async function fetchMyPortfolio(): Promise<{ results: PortfolioRow[] }> {
  return api.get('/specialists/me/portfolio/');
}

export async function uploadPortfolio(
  file: File,
  caption: string,
): Promise<PortfolioRow> {
  const form = new FormData();
  form.append('image', file);
  form.append('caption', caption);
  return api.post('/specialists/me/portfolio/', { body: form });
}

export async function generatePortfolio(
  prompt: string,
  caption: string,
): Promise<PortfolioRow> {
  return api.post('/specialists/me/portfolio/generate/', {
    body: { prompt, caption },
  });
}

export async function updatePortfolio(
  id: number,
  patch: Partial<Pick<PortfolioRow, 'caption' | 'is_active'>>,
): Promise<PortfolioRow> {
  return api.patch(`/specialists/me/portfolio/${id}/`, { body: patch });
}

export async function deletePortfolio(id: number): Promise<void> {
  await api.del(`/specialists/me/portfolio/${id}/`);
}

export async function reorderPortfolio(
  order: number[],
): Promise<{ results: PortfolioRow[] }> {
  return api.post('/specialists/me/portfolio/reorder/', { body: { order } });
}

export async function fetchMyQA(): Promise<{ results: QnARow[] }> {
  return api.get('/specialists/me/qa/');
}

export async function createQA(question: string, answer: string): Promise<QnARow> {
  return api.post('/specialists/me/qa/', { body: { question, answer } });
}

export async function updateQA(
  id: number,
  patch: Partial<Pick<QnARow, 'question' | 'answer' | 'sort_order'>>,
): Promise<QnARow> {
  return api.patch(`/specialists/me/qa/${id}/`, { body: patch });
}

export async function deleteQA(id: number): Promise<void> {
  await api.del(`/specialists/me/qa/${id}/`);
}

export async function fetchMyCategories(): Promise<{ results: CategoryRow[] }> {
  return api.get('/specialists/me/categories/');
}

export async function setMyCategories(
  items: { slug: string; is_primary: boolean }[],
): Promise<{ results: CategoryRow[] }> {
  return api.put('/specialists/me/categories/', { body: { items } });
}

export async function fetchMyCities(): Promise<{ results: CityRow[] }> {
  return api.get('/specialists/me/cities/');
}

export async function setMyCities(
  items: { slug: string; is_primary: boolean }[],
): Promise<{ results: CityRow[] }> {
  return api.put('/specialists/me/cities/', { body: { items } });
}

export async function fetchMyLanguages(): Promise<{ results: LanguageRow[] }> {
  return api.get('/specialists/me/languages/');
}

export async function setMyLanguages(
  codes: string[],
): Promise<{ results: LanguageRow[] }> {
  return api.put('/specialists/me/languages/', { body: { codes } });
}

// ---------------------------------------------------------------------------
// F-170 B0.5 / B3.5 — service-area geo zones (Maplibre map).
// ---------------------------------------------------------------------------

export interface ServiceAreaInput {
  kind: 'reception' | 'delivery_zone' | 'service_area';
  center_lat: number | string;
  center_lng: number | string;
  polygon_geojson?: GeoJSON.Polygon | null;
  radius_km?: number | null;
  address_text?: string;
}

export async function fetchMyServiceAreas(): Promise<{ results: ServiceArea[] }> {
  return api.get('/specialists/me/service-areas/');
}

export async function setMyServiceAreas(
  items: ServiceAreaInput[],
): Promise<{ results: ServiceArea[] }> {
  return api.put('/specialists/me/service-areas/', { body: { items } });
}

export async function fetchServiceAreasBySlug(
  slug: string,
): Promise<{ results: ServiceArea[] }> {
  return api.get(`/specialists/${slug}/service-areas/`);
}
